import { useState, useEffect, useRef } from 'react';
import { detailedCaseStudies } from '../../mocks/agents';
import { useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useCachedData } from '../../hooks/useCachedData';

export default function CaseStudiesPage() {
  const [selectedCase, setSelectedCase] = useState(detailedCaseStudies[0]);
  const navigate = useNavigate();
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Utiliser le cache pour les études de cas
  const { data: cachedCaseStudies } = useCachedData(
    'case-studies-list',
    async () => {
      await new Promise(resolve => setTimeout(resolve, 100));
      return detailedCaseStudies;
    },
    { maxAge: 10 * 60 * 1000 }
  );

  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = `Études de Cas ${selectedCase.company} Paris - AgentIA Pro | Résultats Clients ROI ${selectedCase.results.financial.roi} Agents IA`;

    const setMeta = (name: string, content: string, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (el) { el.setAttribute('content', content); }
      else {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        el.setAttribute('content', content);
        document.head.appendChild(el);
      }
    };

    setMeta('description', `Découvrez comment ${selectedCase.company} a transformé son business avec nos agents IA à Paris. Agents IA conversationnels avec ${selectedCase.results.financial.roi} de ROI, ${selectedCase.results.metrics[0].change} d'amélioration. Résultats mesurables et témoignages clients réels.`);
    setMeta('keywords', `étude de cas IA Paris, ${selectedCase.industry} agent IA, transformation digitale ${selectedCase.industry}, ROI agent IA, témoignage client agent IA, résultats mesurables IA, agents IA conversationnels`);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) { canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/case-studies`); }
    else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/case-studies`);
      document.head.appendChild(canonical);
    }

    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) { lastModified.setAttribute('content', new Date().toISOString().split('T')[0]); }
    else {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
      document.head.appendChild(lastModified);
    }

    const ogTags = [
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/case-studies` },
      { property: 'og:title', content: `Études de Cas ${selectedCase.company} - AgentIA Pro Paris` },
      { property: 'og:description', content: `Comment ${selectedCase.company} a obtenu ${selectedCase.results.financial.roi} de ROI avec nos agents IA.` },
      { property: 'og:image', content: selectedCase.image },
    ];
    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (tag) { tag.setAttribute('content', content); }
      else {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
    });

    // Inline scripts are already in JSX; remove any dynamically added ones
    const dynamicScripts = document.querySelectorAll('script[data-dynamic="true"]');
    dynamicScripts.forEach(s => s.remove());

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.setAttribute('data-dynamic', 'true');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Accueil", "item": import.meta.env.VITE_SITE_URL || 'https://example.com' },
        { "@type": "ListItem", "position": 2, "name": "Études de Cas", "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/case-studies` }
      ]
    });
    document.head.appendChild(breadcrumbScript);
  }, [selectedCase]);

  useEffect(() => {
    // Configuration de l'Intersection Observer pour les animations au scroll
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observer tous les éléments avec la classe 'scroll-animate'
    const elements = document.querySelectorAll('.scroll-animate');
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [selectedCase]);

  const handleAgentsClick = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('agents-grid');
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const displayCaseStudies = cachedCaseStudies || detailedCaseStudies;

  return (
    <div className="min-h-screen bg-white">
      {/* Structured Data for Case Study */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": `Étude de Cas ${selectedCase.company} - Transformation avec AgentIA Pro Paris`,
          "description": selectedCase.context.description,
          "author": {
            "@type": "Organization",
            "name": "AgentIA Pro"
          },
          "publisher": {
            "@type": "Organization",
            "name": "AgentIA Pro",
            "logo": {
              "@type": "ImageObject",
              "url": "https://agentia.pro/logo.png"
            }
          },
          "datePublished": "2024-01-15",
          "dateModified": "2024-01-15",
          "image": selectedCase.image,
          "articleBody": selectedCase.context.description
        })}
      </script>

      <style>{`
        .scroll-animate {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        
        .scroll-animate.animate-in {
          opacity: 1;
          transform: translateY(0);
        }

        .scroll-animate-fade {
          opacity: 0;
          transition: opacity 0.8s ease-out;
        }

        .scroll-animate-fade.animate-in {
          opacity: 1;
        }

        .scroll-animate-scale {
          opacity: 0;
          transform: scale(0.95);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }

        .scroll-animate-scale.animate-in {
          opacity: 1;
          transform: scale(1);
        }

        .scroll-animate-slide-left {
          opacity: 0;
          transform: translateX(-30px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }

        .scroll-animate-slide-left.animate-in {
          opacity: 1;
          transform: translateX(0);
        }

        .scroll-animate-slide-right {
          opacity: 0;
          transform: translateX(30px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }

        .scroll-animate-slide-right.animate-in {
          opacity: 1;
          transform: translateX(0);
        }

        .stagger-1 { transition-delay: 0.1s; }
        .stagger-2 { transition-delay: 0.2s; }
        .stagger-3 { transition-delay: 0.3s; }
        .stagger-4 { transition-delay: 0.4s; }
        .stagger-5 { transition-delay: 0.5s; }
        .stagger-6 { transition-delay: 0.6s; }
      `}</style>

      <Navbar />
      
      {/* Hero Section */}
      <header className="pt-32 pb-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 scroll-animate">
            <strong>Études de Cas Détaillées</strong> - Résultats Clients AgentIA Pro Paris
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto scroll-animate stagger-1">
            Découvrez comment nos clients ont <strong>transformé leur business avec agents IA</strong> et obtenu des <strong>résultats mesurables</strong> avec un <strong>ROI exceptionnel</strong> grâce à nos <strong>agents IA intelligents conversationnels</strong>
          </p>
        </div>
      </header>

      {/* Case Studies Navigation */}
      <section className="py-12 px-6 bg-white border-b">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-4 justify-center">
            {displayCaseStudies.map((caseStudy, index) => (
              <button
                key={caseStudy.id}
                onClick={() => {
                  setSelectedCase(caseStudy);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer scroll-animate stagger-${Math.min(index + 1, 6)} ${
                  selectedCase.id === caseStudy.id
                    ? 'bg-black text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {caseStudy.company}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Selected Case Study Detail */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-16">
            <div className="flex items-start gap-8 mb-8 scroll-animate">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                <i className={`${selectedCase.logo} text-4xl text-gray-800`}></i>
              </div>
              <div className="flex-1">
                <h2 className="text-4xl font-bold mb-3">{selectedCase.company}</h2>
                <div className="flex flex-wrap gap-4 text-gray-600">
                  <span className="flex items-center gap-2">
                    <i className="ri-building-line"></i>
                    {selectedCase.industry}
                  </span>
                  <span className="flex items-center gap-2">
                    <i className="ri-team-line"></i>
                    {selectedCase.size}
                  </span>
                  <span className="flex items-center gap-2">
                    <i className="ri-map-pin-line"></i>
                    {selectedCase.location}
                  </span>
                  <span className="flex items-center gap-2">
                    <i className="ri-time-line"></i>
                    Durée: {selectedCase.duration}
                  </span>
                </div>
                <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-900 to-gray-700 text-white rounded-full text-sm font-medium">
                  <i className="ri-robot-line"></i>
                  {selectedCase.agentUsed}
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="w-full h-96 rounded-3xl overflow-hidden scroll-animate-scale">
              <img 
                src={selectedCase.image} 
                alt={selectedCase.company}
                width="1200"
                height="384"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Context Section */}
          <div className="mb-16 scroll-animate">
            <h3 className="text-3xl font-bold mb-6">{selectedCase.context.title}</h3>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              {selectedCase.context.description}
            </p>
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-6">
              <h4 className="font-bold text-lg mb-4 text-red-900">Défis Principaux</h4>
              <ul className="space-y-3">
                {selectedCase.context.challenges.map((challenge, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-700">
                    <i className="ri-close-circle-fill text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>{challenge}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Solution Section */}
          <div className="mb-16 scroll-animate">
            <h3 className="text-3xl font-bold mb-6">{selectedCase.solution.title}</h3>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              {selectedCase.solution.description}
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6 mb-6">
              <h4 className="font-bold text-lg mb-4 text-blue-900">Mise en Œuvre</h4>
              <ul className="space-y-3">
                {selectedCase.solution.implementation.map((step, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-700">
                    <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                      {index + 1}
                    </div>
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-900 rounded-full font-medium">
              <i className="ri-calendar-check-line"></i>
              {selectedCase.solution.timeline}
            </div>
          </div>

          {/* Results Section */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold mb-8 scroll-animate">{selectedCase.results.title}</h3>
            
            {/* Metrics Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {selectedCase.results.metrics.map((metric, index) => (
                <div 
                  key={index}
                  className={`bg-gradient-to-br from-gray-50 to-white border-2 border-gray-100 rounded-2xl p-6 hover:shadow-lg transition-all scroll-animate stagger-${Math.min(index + 1, 6)}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <i 
                      className={`${metric.icon} text-3xl`}
                      style={{ color: metric.color }}
                    ></i>
                    <span 
                      className="text-2xl font-bold"
                      style={{ color: metric.color }}
                    >
                      {metric.change}
                    </span>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">{metric.label}</h4>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-gray-500">Avant: <strong className="text-gray-700">{metric.before}</strong></span>
                    <i className="ri-arrow-right-line text-gray-400"></i>
                    <span className="text-gray-500">Après: <strong className="text-gray-900">{metric.after}</strong></span>
                  </div>
                </div>
              ))}
            </div>

            {/* Financial Impact */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-8 mb-12 scroll-animate-slide-left">
              <h4 className="text-2xl font-bold mb-6 text-green-900 flex items-center gap-3">
                <i className="ri-money-euro-circle-fill text-3xl"></i>
                Impact Financier
              </h4>
              <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
                <div>
                  <div className="text-sm text-green-700 mb-1">Investissement</div>
                  <div className="text-2xl font-bold text-green-900">{selectedCase.results.financial.investment}</div>
                </div>
                <div>
                  <div className="text-sm text-green-700 mb-1">Économies/mois</div>
                  <div className="text-2xl font-bold text-green-900">{selectedCase.results.financial.monthlySavings}</div>
                </div>
                <div>
                  <div className="text-sm text-green-700 mb-1">Revenus additionnels</div>
                  <div className="text-2xl font-bold text-green-900">{selectedCase.results.financial.additionalRevenue}</div>
                </div>
                <div>
                  <div className="text-sm text-green-700 mb-1">ROI</div>
                  <div className="text-2xl font-bold text-green-900">{selectedCase.results.financial.roi}</div>
                </div>
                <div>
                  <div className="text-sm text-green-700 mb-1">Retour sur investissement</div>
                  <div className="text-2xl font-bold text-green-900">{selectedCase.results.financial.paybackPeriod}</div>
                </div>
              </div>
            </div>

            {/* Operational Results */}
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-2xl p-8 scroll-animate-slide-right">
              <h4 className="text-2xl font-bold mb-6 text-purple-900 flex items-center gap-3">
                <i className="ri-settings-3-fill text-3xl"></i>
                Résultats Opérationnels
              </h4>
              <ul className="grid md:grid-cols-2 gap-4">
                {selectedCase.results.operational.map((result, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-700">
                    <i className="ri-checkbox-circle-fill text-purple-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>{result}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Testimonial */}
          <div className="mb-16 scroll-animate-scale">
            <div className="bg-gradient-to-br from-gray-900 to-gray-700 rounded-3xl p-12 text-white">
              <i className="ri-double-quotes-l text-5xl text-white/30 mb-6"></i>
              <p className="text-2xl leading-relaxed mb-8 font-light">
                {selectedCase.testimonial.quote}
              </p>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-xl font-bold">
                  {selectedCase.testimonial.avatar}
                </div>
                <div>
                  <div className="font-bold text-lg">{selectedCase.testimonial.author}</div>
                  <div className="text-white/70">{selectedCase.testimonial.role}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Takeaways */}
          <div className="scroll-animate">
            <h3 className="text-3xl font-bold mb-6">Points Clés à Retenir</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {selectedCase.keyTakeaways.map((takeaway, index) => (
                <div 
                  key={index}
                  className={`flex items-start gap-4 p-6 bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-xl scroll-animate stagger-${Math.min(index + 1, 6)}`}
                >
                  <div className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    {index + 1}
                  </div>
                  <p className="text-gray-800 font-medium">{takeaway}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-900 to-gray-700">
        <div className="max-w-4xl mx-auto text-center text-white scroll-animate">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Prêt à Obtenir des Résultats Similaires ?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Rejoignez les centaines d'entreprises qui ont transformé leur business avec nos Agents IA
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button 
              onClick={handleAgentsClick}
              className="px-8 py-4 bg-white text-gray-900 rounded-full font-bold text-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
            >
              Démarrer Gratuitement
            </button>
            <button className="px-8 py-4 bg-white/10 text-white border-2 border-white/30 rounded-full font-bold text-lg hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer">
              Parler à un Expert
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
